import produce from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { BehaviorSubject, debounceTime, skip } from 'rxjs';
import { currentUserId } from '../../../../functions/auth/utils';
import { useBehaviorSubjectUpdater } from '../../../../functions/hooks/useBehaviorSubject';
import { useCreation } from '../../../../functions/hooks/useCreation';
import { usePersistFn } from '../../../../functions/hooks/usePersistFn';
import { useServiceFactory } from '../../../../functions/services';
import { ChatSession } from '../models/chatSession';
import { SocketEvent } from '../models/socketEvent';
import { privateMessageEventDataToChatMessage } from '../utils/privateMessageEventDataToChatMessage';
import { useChatGatewayEvent } from './ChatGatewayContext';
import { useActiveChat, useChatStorage } from './GlobalChatContext';

export type SessionListStatus = {
  isRefreshing: boolean;
  isLoading: boolean;
  error: Error | undefined;
  isCached: boolean;
};

export type ChatSessionContextProps = {
  status$: BehaviorSubject<SessionListStatus>;
  sessions$: BehaviorSubject<ChatSession[]>;
  loadSessions: (options?: { refresh?: boolean }) => Promise<void>;
};

export const ChatSessionContext = createContext<ChatSessionContextProps>(
  null as any,
);

export const useChatSessionContext = () => useContext(ChatSessionContext);

const kLimit = 500;

export const ChatSessionContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const storage = useChatStorage();
    const { defaultService } = useServiceFactory();
    const isLoadRequested = useRef(false);
    const activeChat = useActiveChat();
    const activeTargetUserId = activeChat?.targetUserId;

    const sessions$ = useCreation(
      () => new BehaviorSubject<ChatSession[]>([]),
      [],
    );

    const status$ = useCreation(
      () =>
        new BehaviorSubject<SessionListStatus>({
          error: undefined,
          isLoading: false,
          isRefreshing: false,
          isCached: false,
        }),
      [],
    );

    const updateStatus = useBehaviorSubjectUpdater(status$);

    useEffect(() => {
      isLoadRequested.current = false;
      void storage.getSessions().then(sessions => {
        updateStatus(x => ({ ...x, isCached: sessions != null }));
        sessions$.next(sessions ?? []);
      });
    }, [sessions$, storage, updateStatus]);

    useEffect(() => {
      // automatically save sessions to local cache on every change
      const sub = sessions$
        .pipe(skip(1), debounceTime(1000))
        .subscribe(sessions => {
          if (!status$.getValue().isCached) {
            updateStatus(x => ({ ...x, isCached: true }));
          }
          void storage.saveSessions(sessions);
        });
      return () => sub.unsubscribe();
    }, [sessions$, storage, status$, updateStatus]);

    const query = useCallback(async () => {
      const res = await defaultService.chatControllerGetSessions({
        cursor: 0,
        limit: kLimit,
      });
      return res.data.map<ChatSession>(x => ({
        id: x.id,
        sessionId: x.sessionId,
        type: x.type,
        content: x.content,
        avatar: x.avatar,
        targetUserId: x.targetUserId,
        targetUserName: x.targetUserName,
        targetAvatar: x.targetAvatar,
        lastMessageTime: x.lastMessageTime,
        unreadCount: x.unreadCount,
        lastMessage: x.lastMessage
          ? privateMessageEventDataToChatMessage(x.lastMessage as any)
          : undefined,
      }));
    }, [defaultService]);

    const loadSessions = usePersistFn(
      async (options?: { refresh?: boolean }) => {
        if (currentUserId() == null) {
          return;
        }
        isLoadRequested.current = true;
        const statusToUpdate: Partial<SessionListStatus> = {
          error: undefined,
        };

        if (options?.refresh) {
          statusToUpdate.isRefreshing = true;
        } else {
          statusToUpdate.isLoading = true;
        }

        updateStatus(x => ({ ...x, ...statusToUpdate }));

        try {
          const sessions = await query();
          sessions$.next(sessions);
        } catch (e) {
          statusToUpdate.error = e as any;
          console.error('An error occurred while loading chat sessions: ', e);
        } finally {
          if (options?.refresh) {
            statusToUpdate.isRefreshing = false;
          } else {
            statusToUpdate.isLoading = false;
          }
          updateStatus(x => ({ ...x, ...statusToUpdate }));
        }
      },
    );

    const updateSessions = usePersistFn(
      (update: (draft: WritableDraft<ChatSession[]>) => void) => {
        const sessions = sessions$.getValue();
        const updated = produce(sessions, update);
        if (updated !== sessions) {
          sessions$.next(updated);
        }
      },
    );

    useChatGatewayEvent(
      SocketEvent.NewMsg,
      data => {
        const msg = privateMessageEventDataToChatMessage(data);
        const sessions = sessions$.getValue();
        const index = sessions.findIndex(
          item => item.sessionId === msg.sessionId,
        );
        if (index < 0) {
          if (isLoadRequested.current) {
            void loadSessions();
          }
        } else {
          updateSessions(draft => {
            const item = draft[index];
            item.lastMessageTime = new Date(msg.time);
            if (activeTargetUserId === item.targetUserId) {
              if (item.unreadCount > 0) {
                item.unreadCount = 0;
              }
            } else {
              item.unreadCount++;
              item.lastMessage = msg;
            }
            draft.splice(index, 1);
            draft.unshift(item);
          });
        }
      },
      'chat-session-context',
    );

    useEffect(() => {
      if (!activeTargetUserId) return;

      updateSessions(draft => {
        const index = draft.findIndex(
          x => x.targetUserId === activeTargetUserId,
        );
        if (index >= 0) {
          const session = draft[index];
          if (session.unreadCount > 0) {
            session.unreadCount = 0;
          }
        }
      });
    }, [activeTargetUserId, sessions$, updateSessions]);

    const value = useMemo(
      () => ({
        sessions$,
        status$,
        loadSessions,
      }),
      [sessions$, status$, loadSessions],
    );

    return (
      <ChatSessionContext.Provider value={value}>
        {children}
      </ChatSessionContext.Provider>
    );
  },
);
